/**
 * Heatmap value interpolation along a 1D path.
 *
 * Pure numeric helper used by `<XtsStatorHeatmap>` to compute the value
 * at any partPositionMm along the chain, given the consumer's sample
 * points. Behaviour:
 *
 *   - Empty samples → `null`.
 *   - Single sample → that sample's value at every position.
 *   - Position before first sample → first sample's value (clamped).
 *   - Position after last sample → last sample's value (clamped).
 *   - Otherwise → linear interpolation between the two bracketing
 *     samples.
 *
 * Samples must be sorted by `positionMm` ascending — the helper does
 * not sort defensively. Use `sortHeatmapSamples` once when ingesting
 * data.
 */

export interface HeatmapSample {
  positionMm: number;
  value: number;
}

export function sortHeatmapSamples(
  samples: ReadonlyArray<HeatmapSample>,
): HeatmapSample[] {
  return [...samples].sort((a, b) => a.positionMm - b.positionMm);
}

/**
 * Return the linearly interpolated value at `positionMm`. Returns
 * `null` when the sample list is empty.
 */
export function interpolateHeatmapValue(
  samples: ReadonlyArray<HeatmapSample>,
  positionMm: number,
): number | null {
  if (samples.length === 0) return null;
  if (samples.length === 1) return samples[0]!.value;

  const first = samples[0]!;
  const last = samples[samples.length - 1]!;
  if (positionMm <= first.positionMm) return first.value;
  if (positionMm >= last.positionMm) return last.value;

  // Binary search for the bracket — heatmap tubes can have hundreds of
  // segments × dozens of samples per part, linear scan dominates the
  // per-frame time on busy scenes.
  let lo = 0;
  let hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.positionMm <= positionMm) lo = mid;
    else hi = mid;
  }
  const a = samples[lo]!;
  const b = samples[hi]!;
  const span = b.positionMm - a.positionMm;
  if (span === 0) return a.value;
  const t = (positionMm - a.positionMm) / span;
  return a.value + t * (b.value - a.value);
}

/**
 * Map a value into [0..1] across the [min..max] window, clamped at the
 * edges. Used to feed the gradient lerp.
 */
export function normaliseToHeatmapRange(
  value: number,
  min: number,
  max: number,
): number {
  if (max <= min) return 0;
  const t = (value - min) / (max - min);
  if (t < 0) return 0;
  if (t > 1) return 1;
  return t;
}
